import { forwardRef, useRef, useCallback, useEffect } from 'react';
import { IconNames } from '@shipengine/giger-theme';
import { useKeyPress } from '../../../utils/hooks/useKeyPress.js';
import { useMergeRefs } from 'use-callback-ref';
import { useToggle } from 'react-use';
import { Typography } from '../../Typography/Typography.js';
import { ButtonSize, ButtonVariant } from '../../../utils/buttonStyles.js';
import { getFieldInputStyles, getCursorPointerStyles } from '../Field/FieldStyles.js';
import { getFieldContentStyles, getSelectPlaceholderStyles, getOptionStyles, getSelectOptionLeftContentStyles } from './SelectStyles.js';
import { jsxs, Fragment, jsx } from '@emotion/react/jsx-runtime';
import { FieldRightIcon } from '../Field/FieldRightIcon/FieldRightIcon.js';
import { DropdownOptionList } from '../../DropdownOptionList/DropdownOptionList.js';
import { FieldContainer } from '../Field/FieldContainer/FieldContainer.js';
import { FieldSideContent } from '../Field/FieldSideContent/FieldSideContent.js';
import { FieldContent } from '../Field/FieldContent/FieldContent.js';
import { FieldLabel } from '../Field/FieldLabel/FieldLabel.js';
import { ButtonIcon } from '../../ButtonIcon/ButtonIcon.js';
import { Option } from '../../Option/Option.js';

const defaultAriaLabels = {
  toggleOpen: 'Toggle Select'
};
function SelectForwarded(_ref, ref) {
  let {
    clearButtonTooltip,
    children,
    options,
    disabled = false,
    dropdownWidth,
    isOpen,
    leftContent,
    label,
    keyId,
    maxVisibleOptions = 5,
    name,
    optionsHeaderText,
    readOnly = false,
    showRightIcon = true,
    placement = 'bottom-start',
    showLabel = true,
    showPointer = true,
    value,
    isInvalid,
    placeholder,
    hideWhenReferenceNotVisible = true,
    onChange,
    onClear,
    onClickAway,
    ariaLabels,
    ...rest
  } = _ref;
  const labelAvailable = showLabel && !!label;
  const placeholderAvailable = !showLabel && !(value !== null && value !== void 0 && value.label) && placeholder;
  const selectRef = useRef(null);
  const dropdownOptionListRef = useRef(null);
  const mergedSelectRef = useMergeRefs([selectRef, ref]);
  const [isDropdownOpen, toggle] = useToggle(false);
  const hasFocusedValue = isDropdownOpen || (value === null || value === void 0 ? void 0 : value.label) !== undefined;
  const selectAriaLabels = {
    ...defaultAriaLabels,
    ...ariaLabels
  };
  const handleOnChange = value => {
    onChange === null || onChange === void 0 ? void 0 : onChange(name, value || null);
    toggle();
  };
  const handleClickAway = event => {
    var _mergedSelectRef$curr;
    // If we click "inside" the Select do not close the DropdownOptionList
    if ((_mergedSelectRef$curr = mergedSelectRef.current) !== null && _mergedSelectRef$curr !== void 0 && _mergedSelectRef$curr.contains(event.target)) {
      return;
    }
    onClickAway === null || onClickAway === void 0 ? void 0 : onClickAway();
    isDropdownOpen && toggle();
  };
  const handleOnClick = useCallback(() => {
    !disabled && !readOnly && toggle();
  }, [disabled, readOnly, toggle]);
  const handleOnClear = event => {
    onChange === null || onChange === void 0 ? void 0 : onChange(name, null);
    onClear === null || onClear === void 0 ? void 0 : onClear(event);
  };
  const {
    handleKeyPress
  } = useKeyPress({
    onArrowDown: toggle,
    onArrowUp: toggle,
    onEspace: toggle,
    onEnter: toggle
  });
  useEffect(() => {
    if (isOpen) {
      handleOnClick();
    }
  }, [name, isOpen, handleOnClick]);
  return jsxs(Fragment, {
    children: [jsxs(FieldContainer, {
      disabled: disabled,
      isFocused: isDropdownOpen,
      readOnly: readOnly,
      ref: mergedSelectRef,
      tabIndex: 0,
      onClick: handleOnClick,
      onKeyDown: event => {
        var _dropdownOptionListRe;
        return isDropdownOpen ? (_dropdownOptionListRe = dropdownOptionListRef.current) === null || _dropdownOptionListRe === void 0 ? void 0 : _dropdownOptionListRe.onKeyDown(event) : handleKeyPress(event);
      },
      isInvalid: isInvalid,
      ...rest,
      children: [leftContent && jsx(FieldSideContent, {
        children: leftContent
      }), jsxs(FieldContent, {
        css: getFieldContentStyles,
        children: [labelAvailable && jsx(FieldLabel, {
          htmlFor: name,
          hasFocusedValue: hasFocusedValue,
          isDisabled: disabled,
          children: label
        }), placeholderAvailable ? jsx(Typography, {
          css: getSelectPlaceholderStyles,
          variant: "body2",
          children: placeholder
        }) : jsx(Typography, {
          css: getFieldInputStyles(isInvalid, labelAvailable, disabled, readOnly, hasFocusedValue, showPointer && !disabled),
          variant: "body2",
          children: (value === null || value === void 0 ? void 0 : value.label) || '\u00A0'
        }), jsx("input", {
          type: "hidden",
          name: name,
          value: (value === null || value === void 0 ? void 0 : value.value) || ''
        })]
      }), (showRightIcon || onClear) && jsxs(FieldSideContent, {
        align: "right",
        css: showPointer && !disabled ? getCursorPointerStyles : undefined,
        children: [onClear && value && jsx(ButtonIcon, {
          ariaDescribedby: clearButtonTooltip || '',
          icon: IconNames.CANCEL_FILLED,
          size: ButtonSize.SMALL,
          variant: ButtonVariant.TEXT,
          onClick: handleOnClear,
          disabled: disabled,
          children: clearButtonTooltip
        }), showRightIcon && jsx(FieldRightIcon, {
          id: name,
          disabled: disabled,
          iconLabel: selectAriaLabels.toggleOpen,
          iconName: isDropdownOpen ? IconNames.CHEVRON_TOP : IconNames.CHEVRON_BOTTOM
        })]
      })]
    }), jsxs(DropdownOptionList, {
      ref: dropdownOptionListRef,
      isOpen: isDropdownOpen,
      hideWhenReferenceNotVisible: hideWhenReferenceNotVisible,
      reference: mergedSelectRef.current,
      onChange: handleOnChange,
      onClickAway: handleClickAway,
      onEscape: toggle,
      placement: placement,
      optionsHeaderText: optionsHeaderText,
      maxVisibleOptions: maxVisibleOptions,
      dropdownWidth: dropdownWidth,
      children: [children && children, (options === null || options === void 0 ? void 0 : options.length) && options.map((item, index) => jsx(Option, {
        value: item,
        selected: (value === null || value === void 0 ? void 0 : value.value) === item.value,
        hasSublabel: !!item.sublabel,
        children: jsxs("div", {
          css: getOptionStyles,
          children: [item === null || item === void 0 ? void 0 : item.leftContent, jsxs("div", {
            css: getSelectOptionLeftContentStyles(!!(item !== null && item !== void 0 && item.leftContent)),
            children: [item.label, item.sublabel && jsx(Typography, {
              variant: "small",
              children: item.sublabel
            })]
          })]
        })
      }, keyId ? item[keyId] : index))]
    })]
  });
}
SelectForwarded.displayName = 'Select';
const Select = /*#__PURE__*/forwardRef(SelectForwarded);

export { Select };
//# sourceMappingURL=Select.js.map
